import Phaser from 'phaser';
import { BaseScene } from '@core/scenes/BaseScene';
import { Palette } from '@core/config/constants';
import { findCharacter } from '@core/config/characters';
import { RhythmConfig, RHYTHM_DIFFICULTIES } from '@modules/rhythm/config';

/** Escala inicial del anillo de aproximacion (se encoge hasta 1 = momento ideal). */
const RING_START_SCALE = 2.6;
/** Tolerancia (en escala) para considerar el toque como "Perfecto". */
const PERFECT_WINDOW = 0.34;
/** Colores del HUD de combo segun el multiplicador. */
const MULTIPLIER_COLORS = ['#ffffff', '#ffffff', '#4dd0e1', '#ffd54f', '#ff5fa2', '#ff2d8e'];

/**
 * Minijuego de ritmo (pulido): aparecen estrellas con un anillo que se cierra; tocarlas
 * justo cuando el anillo coincide da "Perfecto". El combo sube un multiplicador (x1..x5),
 * hay notas doradas que valen doble y tu personaje baila abajo reaccionando a los aciertos.
 */
export class RhythmScene extends BaseScene {
  private score = 0;
  private combo = 0;
  private remainingMs: number = RhythmConfig.roundDurationMs;
  private isPlaying = false;

  private spawnIntervalMs: number = RhythmConfig.spawnIntervalMs;
  private noteLifetimeMs: number = RhythmConfig.noteLifetimeMs;
  private beatMs = 500;

  private scoreText!: Phaser.GameObjects.Text;
  private comboText!: Phaser.GameObjects.Text;
  private timerText!: Phaser.GameObjects.Text;

  private dancer!: Phaser.GameObjects.Container;
  private dancerGlow!: Phaser.GameObjects.Ellipse;
  private dancerPunch?: Phaser.Tweens.Tween;

  private spawnTimer?: Phaser.Time.TimerEvent;
  private countdownTimer?: Phaser.Time.TimerEvent;
  private beatTimer?: Phaser.Time.TimerEvent;

  public constructor() {
    super(RhythmConfig.key);
  }

  public create(): void {
    this.initResponsive();
    this.addGradientBackground(0x2a0d4e, 0x6a1f73);
    this.addTwinkles(30);
    this.showDifficultyPicker(
      RhythmConfig.title,
      RHYTHM_DIFFICULTIES,
      (index) => this.beginRound(index),
      this.profile.getHighScore(RhythmConfig.key),
    );
  }

  private beginRound(difficultyIndex: number): void {
    const difficulty = RHYTHM_DIFFICULTIES[difficultyIndex];
    this.score = 0;
    this.combo = 0;
    this.remainingMs = difficulty.roundDurationMs;
    this.beatMs = this.audio.getMusicBeatMs();
    this.spawnIntervalMs = Math.round(this.beatMs * difficulty.beatsPerNote);
    this.noteLifetimeMs = Math.round(this.beatMs * difficulty.lifetimeBeats);
    this.isPlaying = false;

    this.addBackButton();
    this.createDancer();

    this.scoreText = this.add
      .text(this.centerX, 30, '', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '32px',
        color: Palette.textLight,
      })
      .setOrigin(0.5, 0);

    this.comboText = this.add
      .text(this.centerX, 70, '', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '30px',
        color: Palette.textLight,
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 0);

    this.timerText = this.add
      .text(this.scale.width - 40, 30, '', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '32px',
        color: Palette.textLight,
      })
      .setOrigin(1, 0);

    this.refreshHud();
    this.startRound();
  }

  private createDancer(): void {
    const { height } = this.scale;
    const size = Math.min(150, height * 0.2);
    const baseY = height - size / 2 - 16;

    // Aura de color que late al compas de la musica (la sombra de contacto la pone el helper).
    this.dancerGlow = this.add.ellipse(this.centerX, baseY + size * 0.42, size * 1.15, size * 0.42, Palette.highlight, 0.22);

    const character = findCharacter(this.profile.character);
    let content: Phaser.GameObjects.GameObject;
    if (character !== undefined) {
      const avatar = this.add.image(0, 0, character.texture).setOrigin(0.5);
      avatar.setScale(size / avatar.height);
      content = avatar;
    } else {
      content = this.add.text(0, 0, '🌟', { fontSize: `${Math.round(size * 0.8)}px` }).setOrigin(0.5);
    }

    this.dancer = this.addCharacterDance(content, { x: this.centerX, y: baseY, size, intensity: 1.1 });
  }

  private startRound(): void {
    this.isPlaying = true;

    this.spawnTimer = this.time.addEvent({
      delay: this.spawnIntervalMs,
      loop: true,
      callback: () => this.spawnNote(),
    });

    this.countdownTimer = this.time.addEvent({
      delay: 100,
      loop: true,
      callback: () => this.tickCountdown(100),
    });

    this.beatTimer = this.time.addEvent({
      delay: this.beatMs,
      loop: true,
      callback: () => this.pulseBeat(),
    });
  }

  private pulseBeat(): void {
    this.tweens.add({
      targets: this.dancerGlow,
      scaleX: 1.15,
      scaleY: 1.2,
      duration: Math.min(180, this.beatMs * 0.4),
      yoyo: true,
      ease: 'Sine.easeOut',
    });
  }

  private tickCountdown(deltaMs: number): void {
    this.remainingMs = Math.max(0, this.remainingMs - deltaMs);
    this.refreshHud();
    if (this.remainingMs <= 0) {
      this.endRound();
    }
  }

  private spawnNote(): void {
    if (!this.isPlaying) {
      return;
    }

    const radius = RhythmConfig.noteRadius;
    const margin = radius + 24;
    const x = Phaser.Math.Between(margin, this.scale.width - margin);
    const y = Phaser.Math.Between(120, this.scale.height * 0.64);
    const isGolden = Math.random() < RhythmConfig.goldenChance;

    const discColor = isGolden ? 0xffd54f : Palette.primary;
    const ringColor = isGolden ? 0xffd54f : Palette.accent;

    const note = this.add.circle(x, y, radius, discColor, 0.25);
    note.setInteractive({ useHandCursor: true });

    const star = this.add.text(x, y, isGolden ? '🌟' : '⭐', { fontSize: '52px' }).setOrigin(0.5);

    const ring = this.add.circle(x, y, radius).setStrokeStyle(6, ringColor, 0.9).setScale(RING_START_SCALE);

    const destroyNote = (): void => {
      note.destroy();
      star.destroy();
      ring.destroy();
    };

    const approach = this.tweens.add({
      targets: ring,
      scaleX: 1,
      scaleY: 1,
      duration: this.noteLifetimeMs,
      ease: 'Linear',
      onComplete: () => {
        if (note.active) {
          this.registerMiss(x, y);
          destroyNote();
        }
      },
    });

    note.once(Phaser.Input.Events.POINTER_DOWN, () => {
      approach.remove();
      const perfect = Math.abs(ring.scaleX - 1) <= PERFECT_WINDOW;
      const gained = this.registerHit(perfect, isGolden);
      this.showHitFeedback(x, y, gained, perfect, isGolden);
      this.spawnRipple(x, y, perfect || isGolden ? 0xffd54f : Palette.accent);
      this.tweens.add({
        targets: [note, star, ring],
        scale: '+=0.7',
        alpha: 0,
        duration: 160,
        onComplete: destroyNote,
      });
    });
  }

  private multiplier(): number {
    return Math.min(RhythmConfig.maxMultiplier, 1 + Math.floor(this.combo / RhythmConfig.comboPerMultiplier));
  }

  private registerHit(perfect: boolean, golden: boolean): number {
    this.combo += 1;
    const multiplier = this.multiplier();
    const base = RhythmConfig.pointsPerHit + (perfect ? RhythmConfig.perfectBonus : 0);
    const gained = base * multiplier * (golden ? RhythmConfig.goldenMultiplier : 1);
    this.score += gained;

    if (perfect || golden) {
      this.audio.playCorrect();
    } else {
      this.audio.playHit();
    }

    this.reactDancer();
    this.bumpCombo();
    this.checkComboMilestone();
    this.refreshHud();
    return gained;
  }

  private reactDancer(): void {
    this.dancerPunch?.stop();
    this.dancer.setScale(1);
    this.dancerPunch = this.tweens.add({
      targets: this.dancer,
      scale: { from: 1.2, to: 1 },
      duration: 240,
      ease: 'Back.easeOut',
    });
  }

  private bumpCombo(): void {
    this.tweens.add({
      targets: this.comboText,
      scale: { from: 1.35, to: 1 },
      duration: 220,
      ease: 'Back.easeOut',
    });
  }

  private checkComboMilestone(): void {
    if (this.combo > 0 && this.combo % 10 === 0) {
      this.audio.playReward();
      this.showComboBanner(this.combo);
    }
  }

  private showComboBanner(combo: number): void {
    const banner = this.add
      .text(this.centerX, this.scale.height * 0.4, `¡Combo x${combo}! 🔥`, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '56px',
        color: '#ffd54f',
        stroke: '#7c4dff',
        strokeThickness: 8,
      })
      .setOrigin(0.5)
      .setAlpha(0)
      .setScale(0.6);

    this.tweens.add({
      targets: banner,
      alpha: { from: 0, to: 1 },
      scale: { from: 0.6, to: 1.1 },
      duration: 280,
      yoyo: true,
      hold: 400,
      ease: 'Back.easeOut',
      onComplete: () => banner.destroy(),
    });
  }

  private spawnRipple(x: number, y: number, color: number): void {
    const ripple = this.add.circle(x, y, RhythmConfig.noteRadius).setStrokeStyle(5, color, 0.9);
    this.tweens.add({
      targets: ripple,
      scale: 2.4,
      alpha: 0,
      duration: 380,
      ease: 'Quad.easeOut',
      onComplete: () => ripple.destroy(),
    });
  }

  private showHitFeedback(x: number, y: number, points: number, perfect: boolean, golden: boolean): void {
    const label = golden ? `🌟 +${points}` : perfect ? `¡Perfecto! +${points}` : `+${points}`;
    const popup = this.add
      .text(x, y - 20, label, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: perfect || golden ? '32px' : '28px',
        color: perfect || golden ? '#ffd54f' : '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.tweens.add({
      targets: popup,
      y: y - 95,
      alpha: 0,
      duration: 680,
      ease: 'Quad.easeOut',
      onComplete: () => popup.destroy(),
    });
  }

  private registerMiss(x: number, y: number): void {
    this.combo = 0;
    this.audio.playMiss();
    this.refreshHud();

    const miss = this.add
      .text(x, y, 'Ups', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '24px',
        color: '#b8a9d9',
      })
      .setOrigin(0.5);
    this.tweens.add({
      targets: miss,
      alpha: 0,
      y: y - 40,
      duration: 500,
      onComplete: () => miss.destroy(),
    });
  }

  private refreshHud(): void {
    const seconds = Math.ceil(this.remainingMs / 1000);
    this.scoreText.setText(`Puntos: ${this.score}`);
    this.timerText.setText(`⏱ ${seconds}s`);

    if (this.combo > 0) {
      const multiplier = this.multiplier();
      this.comboText.setText(`x${multiplier}   •   Combo ${this.combo}`);
      this.comboText.setColor(MULTIPLIER_COLORS[multiplier] ?? '#ffffff');
    } else {
      this.comboText.setText('');
    }
  }

  private endRound(): void {
    this.isPlaying = false;
    this.spawnTimer?.remove();
    this.countdownTimer?.remove();
    this.beatTimer?.remove();

    this.profile.recordPlay();
    const isRecord = this.profile.submitScore(RhythmConfig.key, this.score);
    const coins = Math.floor(this.score / 100) * RhythmConfig.coinsPer100Points;
    this.profile.addCoins(coins);

    this.audio.playReward();
    this.showRoundResults({
      title: isRecord ? '¡Nuevo record!' : '¡Bien hecho!',
      score: this.score,
      coins,
      isRecord,
    });
  }
}
